import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Handshake, Check, X, ExternalLink, ShieldAlert, RefreshCw, PlugZap, Wallet } from "lucide-react";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const LEDGER_STATUS = {
  pending: "bg-[#E8A93C]/15 text-[#8a6410]",
  paid: "bg-green-100 text-green-800",
  reversed: "bg-red-100 text-red-800",
  clawed: "bg-red-100 text-red-800",
};

function LedgerStat({ label, value, tone }) {
  return (
    <Card className="border-zinc-200 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`font-heading text-xl font-bold mt-0.5 ${tone}`}>{value}</p>
    </Card>
  );
}

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

function RejectDialog({ onConfirm, testid }) {
  const [reason, setReason] = useState("");
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" data-testid={testid}>
          <X className="h-4 w-4 mr-1" /> Reject
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reject this application?</AlertDialogTitle>
          <AlertDialogDescription>They'll be emailed and can re-apply after 30 days. Optionally add a reason (shown to the applicant).</AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="reject-reason-input" />
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => onConfirm(reason)} className="bg-red-600 hover:bg-red-700" data-testid="confirm-reject-btn">Reject</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AdjustDialog({ affiliate, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const cents = Math.round(parseFloat(amount) * 100);
    if (!cents || !reason.trim()) { toast.error("Enter a non-zero amount and a reason."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/affiliates/${affiliate.id}/adjust`, { amount_cents: cents, reason: reason.trim() });
      toast.success(`Adjusted ${affiliate.code} by ${cents < 0 ? "-" : "+"}$${Math.abs(cents / 100).toFixed(2)}.`);
      setAmount(""); setReason("");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't adjust."); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`adjust-${affiliate.id}`}>Adjust</Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Manual commission adjustment</AlertDialogTitle>
          <AlertDialogDescription>Credit (positive) or debit (negative) {affiliate.name}'s pending balance. Use dollars, e.g. 12.50 or -5. This is logged to the audit trail and appears in the ledger.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3">
          <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount in $ (e.g. 12.50 or -5)" data-testid="adjust-amount-input" />
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="adjust-reason-input" />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={submit} disabled={busy} className="bg-[#24468C] hover:bg-[#1d3870]" data-testid="adjust-confirm-btn">Apply adjustment</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function AdminAffiliates() {
  const [tab, setTab] = useState("pending");
  const [apps, setApps] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [ledger, setLedger] = useState({ entries: [], summary: {} });
  const [replayId, setReplayId] = useState("");
  const [replaying, setReplaying] = useState(false);
  const [paying, setPaying] = useState(false);
  const [health, setHealth] = useState(null);
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const verifyStripe = async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/admin/affiliates/stripe-health");
      setHealth(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't reach Stripe.");
    } finally { setChecking(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (tab === "affiliates") {
        const { data } = await api.get("/admin/affiliates");
        setAffiliates(data);
      } else if (tab === "ledger") {
        const { data } = await api.get("/admin/affiliates/ledger", { params: { status: "all", limit: 200 } });
        setLedger(data);
      } else {
        const { data } = await api.get("/admin/affiliates/applications", { params: { status: tab } });
        setApps(data);
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  // Live refresh while watching the ledger (great for the first live-commission test).
  useEffect(() => {
    if (tab !== "ledger") return;
    const t = setInterval(() => {
      api.get("/admin/affiliates/ledger", { params: { status: "all", limit: 200 } })
        .then(({ data }) => setLedger(data)).catch(() => {});
    }, 8000);
    return () => clearInterval(t);
  }, [tab]);

  const replay = async () => {
    if (!replayId.trim()) return;
    setReplaying(true);
    try {
      const { data } = await api.post("/admin/affiliates/ledger/replay-invoice", { invoice_id: replayId.trim() });
      toast.success(data.already_processed && data.credited_new === 0
        ? "Invoice already processed — no new commission (idempotent)."
        : `Replayed — ${data.credited_new} new commission credit(s).`);
      setReplayId("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't replay invoice.");
    } finally { setReplaying(false); }
  };

  const runPayouts = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/admin/affiliates/run-payouts");
      toast.success(data.payouts_made > 0 ? `Paid out ${data.payouts_made} affiliate(s).` : "No affiliates at the $50 threshold yet.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't run payouts.");
    } finally { setPaying(false); }
  };

  const approve = async (id) => {
    try {
      await api.post(`/admin/affiliates/applications/${id}/approve`);
      toast.success("Affiliate approved & emailed.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't approve."); }
  };

  const reject = async (id, reason) => {
    try {
      await api.post(`/admin/affiliates/applications/${id}/reject`, { reason });
      toast.success("Application rejected.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't reject."); }
  };

  if (error) {
    return (
      <div className="max-w-md mx-auto py-20 text-center" data-testid="admin-affiliates-locked">
        <ShieldAlert className="h-12 w-12 mx-auto text-amber-500" strokeWidth={1.5} />
        <h1 className="font-heading text-2xl font-bold mt-4">Admin access required</h1>
        <p className="text-muted-foreground mt-2 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-affiliates-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Handshake className="h-6 w-6 text-[#24468C]" strokeWidth={1.75} />
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Affiliate Applications</h1>
        </div>
        <Button variant="outline" onClick={verifyStripe} disabled={checking} data-testid="verify-stripe-btn">
          {checking ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <PlugZap className="h-4 w-4 mr-1.5" strokeWidth={1.75} />} Verify Stripe setup
        </Button>
      </div>

      {health && (
        <div className={`rounded-xl border p-4 ${health.ok ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}`} data-testid="stripe-health-panel">
          <div className="flex items-center justify-between mb-2">
            <p className="font-heading font-semibold text-sm">{health.ok ? "Stripe is ready for payouts ✅" : "Stripe setup incomplete"}</p>
            <Badge className={health.livemode ? "bg-green-600 text-white" : "bg-zinc-200 text-zinc-700"}>{health.livemode ? "LIVE mode" : "TEST mode"}</Badge>
          </div>
          <div className="space-y-1.5">
            {health.checks.map((c, i) => (
              <div key={c.name} className="flex items-start gap-2 text-sm" data-testid={`health-check-${i}`}>
                {c.ok ? <Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" /> : <X className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />}
                <span className="font-medium">{c.name}</span>
                {c.detail && <span className="text-muted-foreground">— {c.detail}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Select value={tab} onValueChange={setTab}>
          <SelectTrigger className="w-56" data-testid="affiliate-tab-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending review</SelectItem>
            <SelectItem value="approved">Approved applications</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="affiliates">Active affiliates</SelectItem>
            <SelectItem value="ledger">Commission ledger</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {tab === "ledger" && !loading && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ledger-summary">
            <LedgerStat label="Pending" value={money(ledger.summary?.pending_cents)} tone="text-[#8a6410]" />
            <LedgerStat label="Paid out" value={money(ledger.summary?.paid_cents)} tone="text-green-600" />
            <LedgerStat label="Clawed back" value={money(ledger.summary?.clawed_cents)} tone="text-red-600" />
            <LedgerStat label="Entries" value={ledger.summary?.count ?? 0} tone="text-[#24468C]" />
          </div>
          <div className="flex flex-wrap items-end gap-2 rounded-lg border border-zinc-200 bg-white p-4">
            <div className="flex-1 min-w-[220px]">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Replay a Stripe invoice</p>
              <Input value={replayId} onChange={(e) => setReplayId(e.target.value)} placeholder="in_1AbC... (re-drive if a webhook was missed)" data-testid="replay-invoice-input" />
            </div>
            <Button onClick={replay} disabled={replaying || !replayId.trim()} className="bg-[#24468C] hover:bg-[#1d3870]" data-testid="replay-invoice-btn">
              {replaying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Replay"}
            </Button>
            <Button variant="outline" onClick={runPayouts} disabled={paying} data-testid="run-payouts-btn">
              {paying ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Wallet className="h-4 w-4 mr-1.5" strokeWidth={1.75} />} Run payouts now
            </Button>
            <span className="text-xs text-muted-foreground flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Auto-refreshing every 8s</span>
          </div>
        </>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : tab === "ledger" ? (
        ledger.entries.length === 0 ? <Empty label="No commission entries yet. They'll appear here as invoices are paid." /> : (
          <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid="ledger-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead><TableHead>Affiliate</TableHead><TableHead>Tier</TableHead>
                  <TableHead>Subscriber</TableHead><TableHead>Plan</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledger.entries.map((e) => (
                  <TableRow key={e.id} data-testid={`ledger-row-${e.id}`}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{(e.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                    <TableCell className="font-mono text-xs">{e.affiliate_code}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">T{e.tier}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{e.subscriber_email_masked}</TableCell>
                    <TableCell className="text-sm">{e.plan || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className={`text-sm font-medium ${e.amount_cents < 0 ? "text-red-600" : ""}`}>{money(e.amount_cents)}</TableCell>
                    <TableCell><Badge className={LEDGER_STATUS[e.status] || "bg-zinc-100 text-zinc-600"}>{e.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      ) : tab === "affiliates" ? (
        affiliates.length === 0 ? <Empty label="No affiliates yet." /> : (
          <div className="space-y-3">
            {affiliates.map((a) => (
              <Card key={a.id} className="border-zinc-200 p-4 flex items-center justify-between" data-testid={`affiliate-row-${a.id}`}>
                <div>
                  <p className="font-medium">{a.name} <span className="text-muted-foreground text-sm">· {a.email}</span></p>
                  <p className="text-sm text-muted-foreground mt-0.5">Code <strong className="text-[#24468C] tracking-widest">{a.code}</strong> · {a.payout_country} · pending ${((a.commission_pending_cents || 0) / 100).toFixed(2)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <AdjustDialog affiliate={a} onDone={load} />
                  <Badge className={a.payouts_enabled ? "bg-green-100 text-green-800" : "bg-zinc-100 text-zinc-600"}>{a.payouts_enabled ? "Payouts on" : "No payout setup"}</Badge>
                </div>
              </Card>
            ))}
          </div>
        )
      ) : apps.length === 0 ? (
        <Empty label={`No ${tab} applications.`} />
      ) : (
        <div className="space-y-3">
          {apps.map((app) => (
            <Card key={app.id} className="border-zinc-200 p-4" data-testid={`application-row-${app.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{app.name}</p>
                    <Badge className={STATUS_STYLE[app.status]}>{app.status}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{app.email} · {app.payout_country}</p>
                  <p className="text-sm mt-2">{app.audience}</p>
                  {app.links?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {app.links.map((l, i) => (
                        <a key={l} href={l} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#24468C] hover:underline" data-testid={`app-link-${app.id}-${i}`}>
                          <ExternalLink className="h-3 w-3" /> {l.replace(/^https?:\/\//, "").slice(0, 40)}
                        </a>
                      ))}
                    </div>
                  )}
                  {app.reason && <p className="text-xs text-red-600 mt-2">Reason: {app.reason}</p>}
                </div>
                {app.status === "pending" && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => approve(app.id)} data-testid={`approve-${app.id}`}>
                      <Check className="h-4 w-4 mr-1" /> Approve
                    </Button>
                    <RejectDialog onConfirm={(r) => reject(app.id, r)} testid={`reject-${app.id}`} />
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-10 text-center text-muted-foreground" data-testid="admin-affiliates-empty">
      {label}
    </div>
  );
}
